/**
 * One source for the pricing story, shared by the landing page and /pricing.
 * Two pages with two feature lists and two taglines is how they ended up
 * contradicting each other.
 */

export const TAGLINE = 'Grow with confidence, know exactly what to do and when';

export const TRIAL_DAYS = 14;
export const PRICE_ANNUAL = '$49.99';
export const PRICE_MONTHLY = '$5.99';

export const FREE_FEATURES = [
  'Full plant library (240+ plants)',
  'Complete pest and disease guide',
  'Which fruit tree varieties pollinate each other',
  'Seasonal planting calendar',
  'Zone detection',
  'Unlimited garden journal entries, with photos',
  'Garden planner (1 plan, 1 bed)',
  '5 garden questions per month',
  'Pizza Garden template',
];

/** The four that carry the promise. Everything else sits behind the expander. */
export const PRO_HEADLINE_FEATURES = [
  'Weekly planting and timing guidance',
  'Personalized garden tasks',
  'Unlimited garden planner, as many beds and plans as you need',
  'Photo plant diagnosis',
];

export const PRO_MORE_FEATURES = [
  'Detailed seed-starting and growing instructions',
  'Companion planting overlay',
  'Succession planting guidance',
  'Native interplanting guidance',
  'Head-to-head fruit tree comparison and orchard planning',
  'All garden templates',
  'Print and export your plans',
  'Submit your own plant photos',
  '50 garden questions per month',
  'Advanced guides (pruning, grafting, fruit tree care)',
  'In-depth preservation and food storage guidance',
  'Survival crop planning and long-term food strategies',
  'Priority access to new features',
];

/**
 * A visitor who picks monthly on the landing page has not signed up yet, so the
 * choice is carried in sessionStorage and honoured when they reach /pricing.
 */
export const PREFERRED_PLAN_KEY = 'garden-preferred-plan';

export function rememberPreferredPlan(plan: 'annual' | 'monthly'): void {
  try { sessionStorage.setItem(PREFERRED_PLAN_KEY, plan); } catch {}
}

export function readPreferredPlan(): 'annual' | 'monthly' | null {
  try {
    const value = sessionStorage.getItem(PREFERRED_PLAN_KEY);
    return value === 'monthly' || value === 'annual' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Someone who clicks "start my trial" on the landing page has no account yet.
 * The intent rides through signup and onboarding so they end up where they
 * asked to go, instead of being dropped on the dashboard like everyone else.
 */
export const SIGNUP_INTENT_KEY = 'garden-signup-intent';

export function rememberSignupIntent(intent: 'trial' | 'free'): void {
  try { sessionStorage.setItem(SIGNUP_INTENT_KEY, intent); } catch {}
}

export function readSignupIntent(): 'trial' | 'free' | null {
  try {
    const value = sessionStorage.getItem(SIGNUP_INTENT_KEY);
    return value === 'trial' || value === 'free' ? value : null;
  } catch {
    return null;
  }
}

export function clearSignupIntent(): void {
  try { sessionStorage.removeItem(SIGNUP_INTENT_KEY); } catch {}
}
